// weather/icons.jsx — the weather glyphs, line-drawn on a 24px grid at
// the panel's 1.5 stroke. Always ink (currentColor): the redesign took
// the orange off the sun because orange no longer means anything here.
//
// Keys come from model.js (iconForDescriptor / iconForCondition). BOM
// uses about fifteen descriptors; the rarer ones share a glyph.
//
// `bg` is the colour of the surface the icon sits on. The cloud in the
// part-sun and part-moon glyphs is filled with it so the sun or moon
// reads as behind the cloud rather than through it.

import * as React from 'react';

// A cloud whose base runs along y = 17. `lift` raises it to make room
// for rain, lightning or fog underneath.
const cloud = (lift = 0) =>
  `M7 ${17 - lift}h10.5a3.5 3.5 0 0 0 .6-6.95A5.5 5.5 0 0 0 7.4 8.6 4.2 4.2 0 0 0 7 ${17 - lift}z`;

// The smaller cloud of part-sun / part-moon, sitting lower right.
const SMALL_CLOUD = 'M9.5 20h8a3.2 3.2 0 0 0 .5-6.35 4.6 4.6 0 0 0-8.75-1.25A3.8 3.8 0 0 0 9.5 20z';

const RAYS = (cx, cy, r1, r2, skip = []) =>
  [0, 45, 90, 135, 180, 225, 270, 315]
    .filter((a) => !skip.includes(a))
    .map((a) => {
      const rad = (a * Math.PI) / 180;
      const x1 = cx + r1 * Math.cos(rad), y1 = cy + r1 * Math.sin(rad);
      const x2 = cx + r2 * Math.cos(rad), y2 = cy + r2 * Math.sin(rad);
      return `M${x1.toFixed(2)} ${y1.toFixed(2)}L${x2.toFixed(2)} ${y2.toFixed(2)}`;
    })
    .join('');

const MOON = 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z';
const SMALL_MOON = 'M13.6 10.2A5 5 0 0 1 7.3 3.9a5 5 0 1 0 6.3 6.3z';

function glyph(name, bg) {
  switch (name) {
    case 'sun':
      return <><circle cx="12" cy="12" r="4"/><path d={RAYS(12, 12, 6.5, 9)}/></>;
    case 'moon':
      return <path d={MOON}/>;
    case 'partSun':
      return <>
        <circle cx="8.5" cy="8.5" r="3"/>
        <path d={RAYS(8.5, 8.5, 5, 7, [45, 90, 0])}/>
        <path d={SMALL_CLOUD} fill={bg}/>
      </>;
    case 'partMoon':
      return <><path d={SMALL_MOON}/><path d={SMALL_CLOUD} fill={bg}/></>;
    case 'cloud':
      return <path d={cloud()}/>;
    case 'drizzle':
      return <><path d={cloud(3)}/><path d="M9 17.5l-.5 1.5M15 17.5l-.5 1.5M12 19.5l-.5 1.5"/></>;
    case 'rain':
      return <><path d={cloud(3)}/><path d="M8.5 17l-1 2.6M12.5 17l-1.5 4M16.5 17l-1 2.6"/></>;
    case 'heavy':
      return <><path d={cloud(3)}/><path d="M7.5 17l-1.2 3.2M11 17l-1.6 4.5M14.5 17l-1.6 4.5M18 17l-1.2 3.2"/></>;
    case 'storm':
      return <><path d={cloud(3)}/><path d="M12.5 15.5l-2.2 3.6h3.2l-2 3.4"/></>;
    case 'fog':
      return <><path d={cloud(4)}/><path d="M4 16.5h16M6 19.5h12"/></>;
    case 'snow':
      return <><path d={cloud(3)}/><path d="M9 17.5v3M7.7 18.2l2.6 1.6M10.3 18.2l-2.6 1.6M15 17.5v3M13.7 18.2l2.6 1.6M16.3 18.2l-2.6 1.6"/></>;
    case 'wind':
      return <path d="M3 9h10.5a2.75 2.75 0 1 0-2.75-2.75M3 15h14.5a2.75 2.75 0 1 1-2.75 2.75M3 12h7"/>;
    case 'haze':
      return <><path d="M6.5 14a5.5 5.5 0 0 1 11 0"/><path d={RAYS(12, 14, 7.5, 9.5, [0, 45, 90, 135])}/><path d="M3 17h18M5 20h14"/></>;
    default:
      return <path d={cloud()}/>;
  }
}

export function WeatherIcon({ name, size = 22, bg = 'transparent', title, strokeWidth = 1.5 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}
      style={{ display: 'block', flexShrink: 0 }}>
      {glyph(name, bg)}
    </svg>
  );
}
